import gsap from "gsap";
import { module as Module } from "modujs";
import { loadImage } from "../utils/image";

const START_FRAME = 0;
const AUTOPLAY_STEP = 0.01;

Promise.pending = Promise.race.bind(Promise, []);

export default class extends Module {
    constructor(m) {
        super(m);

        const imagesPath = this.getData("images-path");

        if (!imagesPath) {
            console.warn(
                "Please add the sequence path in data-images-sequence-images-path"
            );
            return;
        }

        // Binding
        this.onResizeBind = this.onResize.bind(this);
        this.onFontsLoadedBind = this.onFontsLoaded.bind(this);
        this.onUpdateBind = this.onUpdate.bind(this);

        // DOM
        this.$el = this.$("ref")[0];
        this.$canvas = this.$("canvas")[0];

        // Metrics
        this.width = this.$el.offsetWidth;
        this.height = this.$el.offsetHeight;
        this.dpr = window.devicePixelRatio;

        // Options
        this.progress = 0;
        this.autoPlayDirection = 0;
        this.autoPlayProgress = 0;
        this.preloadStep = 16;
        this.isSequenceReady = false;

        // Images
        this.images = {
            path: imagesPath,
            exension: ".jpg",
            totalLength: 180,
            framesToLoad: [],
            framesLoaded: [],
        };

        // Autoplay
        this.autoplay = this.getData("autoplay") || false;

        // RAF
        this.raf = {
            framerate: (1 / 60) * 1000,
            deltaTime: 0,
            time: performance.now(),
            lt: performance.now(),
            elapsedInterval: 0,
        };
    }

    ///////////////
    // Lifecyle
    ///////////////
    async init() {
        // Set canvas
        this.updateSize();

        // Binding
        this.bindEvents();

        if (window.isFontsLoaded) {
            this.onFontsLoaded();
        }

        // Set images
        this.setImages();

        // Load sequence
        await this.loadSequence(true);
        this.isSequenceReady = true;

        // Update
        this.onUpdate();

        if (this.autoplay) {
            setTimeout(() => {
                this.play();
            }, 1000);
        }
    }

    destroy() {
        super.destroy();
        this.aborted = true;
        this.unbindEvents();
        this.onLeave();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener("resizeEnd", this.onResizeBind);
        window.addEventListener("fontsLoaded", this.onFontsLoadedBind);
    }

    unbindEvents() {
        window.removeEventListener("resizeEnd", this.onResizeBind);
        window.removeEventListener("fontsLoaded", this.onFontsLoadedBind);
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        this.updateSize();
    }

    onFontsLoaded() {
        this.updateSize();
    }

    onUpdate() {
        if (!this.isPlaying || !this.isSequenceReady) return;

        this.raf.time = performance.now();
        this.raf.deltaTime = this.raf.time - this.raf.lt;
        this.raf.elapsedInterval += this.raf.deltaTime;

        if (this.raf.elapsedInterval >= this.raf.framerate) {
            this.raf.elapsedInterval = 0;

            // Current frame
            this.setCurrentFrame();

            // Get image then draw it
            this.drawImage();
        }

        this.raf.lt = this.raf.time;
    }

    onProgress(progress) {
        this.progress = progress;
    }

    onEnter() {
        this.play();
    }

    onLeave() {
        this.pause();
    }

    ///////////////
    // Methods
    ///////////////
    updateSize() {
        this.width = this.$el.offsetWidth;
        this.height = this.$el.offsetHeight;
        this.$canvas.width = Math.ceil((this.dpr * this.width) / 4.0) * 4.0;
        this.$canvas.height = Math.ceil((this.dpr * this.height) / 4.0) * 4.0;
        this.$canvas.style = `width: ${this.width}px; height: ${this.height}px;`;

        this.ctx = this.$canvas.getContext("2d");

        this.onUpdate();
    }

    play() {
        if (this.isPlaying) return;
        this.isPlaying = true;
        gsap.ticker.add(this.onUpdateBind);
    }

    pause() {
        if (!this.isPlaying) return;
        this.isPlaying = false;
        gsap.ticker.remove(this.onUpdateBind);
    }

    setImages() {
        for (let i = 0; i <= this.images.totalLength; i++) {
            this.images.framesToLoad[i] = {
                index: i,
                src: `${this.images.path}${this._pad(i, 3)}${
                    this.images.exension
                }`,
            };
        }
    }

    loadSequence(firstLoop = true) {
        return new Promise(async (resolve, reject) => {
            const imagesToLoad = [];
            const step = this.preloadStep;

            let index = 0;
            while (index < this.images.framesToLoad.length) {
                if (this.aborted) {
                    resolve();
                    return;
                }
                const frame = this.images.framesToLoad[index];
                if (
                    (firstLoop && index % step === 0) ||
                    (!firstLoop &&
                        index > 0 &&
                        index % step === 0 &&
                        (index / 2) % step !== 0)
                ) {
                    const imagePromise = await loadImage(frame.src, {
                        index: frame.index,
                    });
                    imagesToLoad.push(imagePromise);
                }
                index++;
            }

            const loadedFrames = [...this.images.framesLoaded, ...imagesToLoad];
            loadedFrames.sort((a, b) => a.index - b.index);
            this.images.framesLoaded = loadedFrames;

            resolve();

            if (step > 1) {
                this.preloadStep = step / 2;

                requestAnimationFrame(() => {
                    this.loadSequence(false);
                });
            } else {
                //console.log('All frames are loaded');
            }
        });
    }

    setCurrentFrame() {
        this.currentFrame =
            (START_FRAME +
                Math.min(
                    Math.floor(this.progress * this.images.framesLoaded.length),
                    this.images.framesLoaded.length - 1
                )) %
            this.images.framesLoaded.length;
    }

    drawImage() {
        const image = this.images.framesLoaded[this.currentFrame];
        const scaleX = this.$canvas.width / image.width;
        const scaleY = this.$canvas.height / image.height;
        const scale = Math.max(scaleX, scaleY);

        const width = image.width * scale;
        const height = image.height * scale;

        const x = (this.$canvas.width - width) * 0.5;
        const y = (this.$canvas.height - height) * 0.5;
        this.ctx.clearRect(0, 0, this.$canvas.width, this.$canvas.height);
        this.ctx.drawImage(image.element, x, y, width, height);

        if (this.autoplay) {
            this.autoPlayProgress = this.autoPlayProgress + AUTOPLAY_STEP;

            if (Math.trunc(this.autoPlayProgress) % 2 == 0) {
                const progress = (this.progress + AUTOPLAY_STEP) % 1;
                this.progress =
                    Math.round((progress + Number.EPSILON) * 100) / 100;

                if (this.autoPlayDirection != 1) {
                    this.autoPlayDirection = 1;
                    this.progress =
                        Math.round((0 + Number.EPSILON) * 100) / 100;
                }
            } else {
                const progress = (1 + ((this.progress - AUTOPLAY_STEP) % 1)) % 1;
                this.progress =
                    Math.round((progress + Number.EPSILON) * 100) / 100;

                if (this.autoPlayDirection != -1) {
                    this.autoPlayDirection = -1;
                    this.progress =
                        Math.round((1 + Number.EPSILON) * 100) / 100;
                }
            }
        }
    }

    // Prepend leading 0 to number
    _pad(x, width) {
        return String(x).padStart(width, "0");
    }
}
